import "./login.css";

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import RareBoxLogo from "../../../Logos/RareBoxLogo";
import { useEffect, useRef, useState } from "react";
import { GlobalState } from "../../../context/GlobalContext";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { parseErrorMessage } from "utils/ErrorFunctions/parseErrorMessage";
import ForgotPasswordModal from "./ForgotPasswordModal/ForgotPasswordModal";
import {
	getDeviceCredential,
	platformAuthenticatorAvailable,
	serializeAssertion,
} from "utils/webauthn";

const getAuth2Payload = (data) => data?.result;

const getChallengeIdFromResponse = (data) => {
	const payload = getAuth2Payload(data);
	return payload?.challengeId || payload?.challenge_id || payload?.id || "";
};

const getExpiryFromResponse = (data) => {
	const payload = getAuth2Payload(data);
	return payload?.expiresAt || payload?.expires_at || "";
};

const getMfaFromResponse = (data) => {
	const payload = getAuth2Payload(data);
	if (payload?.mfa === "totp" || payload?.mfa === "webauthn") return payload.mfa;
	return "email";
};

const getDeviceFromResponse = (data) => getAuth2Payload(data)?.device || null;

const getModesFromResponse = (data) => {
	const modes = getAuth2Payload(data)?.modes;
	if (!Array.isArray(modes)) return [];
	return modes.filter((mode) => mode === "webauthn" || mode === "totp" || mode === "email");
};

const getUserFromVerifyResponse = (data) => {
	const payload = getAuth2Payload(data);
	return payload?.user || data?.user;
};

const getPyTokenFromVerifyResponse = (data) => {
	const payload = getAuth2Payload(data);
	return payload?.pyToken || payload?.py_token || data?.pyToken || data?.py_token;
};

const Login = () => {
	const { snackBar, setSnackBar, setSnackBarOpen, setLoginUser, setLoadingOpen } = GlobalState();
	const navigate = useNavigate();
	const [open, setOpen] = useState(false);

	useEffect(() => {
		if (localStorage.getItem("user_token")) navigate("/explore");
		setDeviceReady(Boolean(window.PublicKeyCredential));
		platformAuthenticatorAvailable().then((available) => {
			if (available) setDeviceReady(true);
		});
	}, []);

	const [userName, setUserName] = useState("");
	const [password, setPassword] = useState("");
	const [otp, setOtp] = useState("");
	const [challengeId, setChallengeId] = useState("");
	const [expiresAt, setExpiresAt] = useState("");
	const [step, setStep] = useState("login");
	const [useBackupCode, setUseBackupCode] = useState(false);
	const [loginModes, setLoginModes] = useState(["email"]);
	const [deviceOptions, setDeviceOptions] = useState(null);
	const [deviceReady, setDeviceReady] = useState(false);
	const [deviceNeedsRetry, setDeviceNeedsRetry] = useState(false);
	const devicePrompted = useRef("");

	const showSnackBar = (message, severity = "error") => {
		setSnackBarOpen(true);
		setSnackBar({ ...snackBar, message, severity });
	};

	const resetOtpState = () => {
		setOtp("");
		setChallengeId("");
		setExpiresAt("");
		setUseBackupCode(false);
		setLoginModes(["email"]);
		setDeviceOptions(null);
		setDeviceNeedsRetry(false);
		devicePrompted.current = "";
		setStep("login");
	};

	const applyChallenge = (data) => {
		const nextChallengeId = getChallengeIdFromResponse(data);
		if (!nextChallengeId) {
			throw new Error("Login response did not include challengeId");
		}
		const modes = getModesFromResponse(data);
		const mfa = getMfaFromResponse(data);
		setChallengeId(nextChallengeId);
		setExpiresAt(getExpiryFromResponse(data));
		if (modes.length) setLoginModes(modes);
		setDeviceOptions(getDeviceFromResponse(data));
		setDeviceNeedsRetry(false);
		setOtp("");
		setUseBackupCode(false);
		setStep(mfa === "webauthn" ? "device" : mfa === "totp" ? "totp" : "otp");
		return mfa;
	};

	const completeLogin = (user, pyToken) => {
		setLoginUser(user);
		localStorage.setItem("user_token", user.token);
		localStorage.setItem("user", JSON.stringify(user));
		axios.defaults.headers.common["Authorization"] = `Bearer ${user.token}`;
		if (pyToken) {
			localStorage.setItem("pyToken", pyToken);
		}
		navigate("/explore");
	};

	const formatExpiry = (value) => {
		if (!value) return "";
		const date = new Date(value);
		if (Number.isNaN(date.getTime())) return "";
		return date.toLocaleString();
	};

	const handleLoginClick = async (e) => {
		e.preventDefault();
		setLoadingOpen(true);
		if (userName === "" || password === "") {
			showSnackBar("Enter both email and password");
			setLoadingOpen(false);
			return;
		}

		try {
			const res = await axios.post("/api/auth2/login", { email: userName, password });
			applyChallenge(res?.data);
		} catch (error) {
			showSnackBar(parseErrorMessage(error));
		} finally {
			setLoadingOpen(false);
		}
	};

	const handleVerifyOtp = async (e) => {
		e.preventDefault();
		if (otp.trim() === "") {
			showSnackBar("Enter the OTP sent to your email");
			return;
		}

		setLoadingOpen(true);
		try {
			const res = await axios.post("/api/auth2/verify_otp", {
				challengeId,
				otp: otp.trim(),
			});
			const verifiedUser = getUserFromVerifyResponse(res?.data);
			const pyToken = getPyTokenFromVerifyResponse(res?.data);
			if (!verifiedUser?.token) {
				throw new Error("OTP verification response did not include user token");
			}
			completeLogin(verifiedUser, pyToken);
		} catch (error) {
			showSnackBar(parseErrorMessage(error));
		} finally {
			setLoadingOpen(false);
		}
	};

	const handleVerifyTotp = async (e) => {
		e.preventDefault();
		if (otp.trim() === "") {
			showSnackBar(useBackupCode ? "Enter a backup code" : "Enter the code from your authenticator app");
			return;
		}

		setLoadingOpen(true);
		try {
			const res = await axios.post("/api/auth2/verify_totp", {
				challengeId,
				code: otp.trim(),
			});
			const verifiedUser = getUserFromVerifyResponse(res?.data);
			const pyToken = getPyTokenFromVerifyResponse(res?.data);
			if (!verifiedUser?.token) {
				throw new Error("Authenticator verification response did not include user token");
			}
			completeLogin(verifiedUser, pyToken);
		} catch (error) {
			showSnackBar(parseErrorMessage(error));
		} finally {
			setLoadingOpen(false);
		}
	};

	const handleDeviceSignIn = async () => {
		if (!deviceOptions) {
			showSnackBar("Device sign-in is not ready. Choose it again.");
			return;
		}
		setDeviceNeedsRetry(false);
		setLoadingOpen(true);
		try {
			const credential = await getDeviceCredential(deviceOptions);
			if (!credential) throw new Error("This device did not return a credential");
			const res = await axios.post("/api/auth2/webauthn/login/verify", {
				challengeId,
				credential: serializeAssertion(credential),
			});
			const verifiedUser = getUserFromVerifyResponse(res?.data);
			const pyToken = getPyTokenFromVerifyResponse(res?.data);
			if (!verifiedUser?.token) throw new Error("Device sign-in did not include a user token");
			completeLogin(verifiedUser, pyToken);
		} catch (error) {
			setDeviceNeedsRetry(true);
			if (error?.name === "NotAllowedError") showSnackBar("Device sign-in was cancelled");
			else showSnackBar(parseErrorMessage(error));
		} finally {
			setLoadingOpen(false);
		}
	};

	const deviceSignInRef = useRef(handleDeviceSignIn);
	deviceSignInRef.current = handleDeviceSignIn;

	useEffect(() => {
		if (step !== "device" || !deviceOptions || !challengeId) return;
		if (devicePrompted.current === challengeId) return;
		devicePrompted.current = challengeId;
		deviceSignInRef.current();
	}, [step, deviceOptions, challengeId]);

	const chooseMode = async (mode) => {
		if (mode === "webauthn" && !loginModes.includes("webauthn")) {
			setStep("device");
			setDeviceOptions(null);
			return;
		}
		const current = step === "device" ? "webauthn" : step === "totp" ? "totp" : "email";
		if (current === mode && (mode !== "webauthn" || deviceOptions)) return;
		await handleSwitchMode(mode);
	};

	const handleSwitchMode = async (mode) => {
		setLoadingOpen(true);
		try {
			const res = await axios.post("/api/auth2/switch_login_mode", { challengeId, mode });
			applyChallenge(res?.data);
		} catch (error) {
			showSnackBar(parseErrorMessage(error));
		} finally {
			setLoadingOpen(false);
		}
	};

	const handleResendOtp = async () => {
		setLoadingOpen(true);
		try {
			const res = await axios.post("/api/auth2/resend_otp", { challengeId });
			applyChallenge(res?.data);
			showSnackBar(res.data.message || "OTP resent to your email", "success");
		} catch (error) {
			showSnackBar(parseErrorMessage(error));
		} finally {
			setLoadingOpen(false);
		}
	};

	return (
		<Box className="login-page-shell" display="flex" alignItems="center" justifyContent="center">
			<Box
				className="login-layout"
				sx={{
					width: { xs: "100%", md: "min(1120px, 92vw)" },
					minHeight: { xs: "calc(100dvh - 36px)", md: "78vh" },
				}}>
				<Box className="login-brand-panel" display={{ xs: "none", md: "flex" }}>
					<Box className="login-brand-inner">
						<Box className="login-brand-logo">
							<RareBoxLogo width="100%" />
						</Box>
					</Box>
				</Box>
				<Box component="section" className="login-form-panel">
					<Box className={`login-form-wrap${step === "login" ? "" : " is-verify"}`}>
						<Box sx={{ width: { xs: "180px", md: "160px" } }} display={{ xs: "block", md: "none" }} alignSelf="center">
							<RareBoxLogo width="100%" />
						</Box>
						<Stack spacing={0.75}>
							<Typography className="login-form-title">
								{step === "login"
									? "Sign in"
									: step === "otp"
									? "Check your email"
									: step === "device"
									? "Use this device"
									: useBackupCode
									? "Backup code"
									: "Authenticator app"}
							</Typography>
							{step === "otp" ? (
								<Typography className="login-form-subtitle">{`We sent a 6-digit code to ${userName}.`}</Typography>
							) : null}
							{step === "device" ? (
								<Typography className="login-form-subtitle">
									Confirm with the fingerprint, PIN, or password already set on this laptop.
								</Typography>
							) : null}
							{step === "totp" ? (
								<Typography className="login-form-subtitle">
									{useBackupCode
										? "Enter one of the codes you saved when you turned on the authenticator."
										: "Open your authenticator app and enter the current 6-digit code."}
								</Typography>
							) : null}
						</Stack>
						<Stack
							component="form"
							onSubmit={step === "login" ? handleLoginClick : step === "device" ? (e) => e.preventDefault() : step === "totp" ? handleVerifyTotp : handleVerifyOtp}
							className="login-form-card"
							spacing={step === "login" ? 2.25 : 1.5}>
							{step !== "login" ? (
								<Box className="login-mode-switch" role="tablist" aria-label="Sign-in method">
									<button
										type="button"
										role="tab"
										className={step === "otp" ? "is-active" : ""}
										onClick={() => chooseMode("email")}>
										Email
									</button>
									<button
										type="button"
										role="tab"
										className={step === "totp" ? "is-active" : ""}
										onClick={() => chooseMode("totp")}
										disabled={!loginModes.includes("totp")}>
										Authenticator
									</button>
									<button
										type="button"
										role="tab"
										className={step === "device" ? "is-active" : ""}
										onClick={() => chooseMode("webauthn")}
										disabled={!deviceReady}>
										This device
									</button>
								</Box>
							) : null}
							{step === "login" ? (
								<>
									<TextField
										onChange={(e) => setUserName(e.target.value)}
										value={userName}
										size="small"
										id="login-email-input"
										label="Email"
										type="text"
										autoComplete=""
										fullWidth
										className="login-input"
									/>
									<TextField
										value={password}
										onChange={(e) => setPassword(e.target.value)}
										size="small"
										id="login-password-input"
										label="Password"
										type="password"
										autoComplete=""
										fullWidth
										className="login-input"
									/>
									<Button variant="contained" fullWidth type="submit" className="login-primary-btn">
										Continue
									</Button>
									<Typography className="login-link-row">
										Forgot password?
										<span className="login-inline-link" onClick={() => setOpen(true)}>
											Reset
										</span>
									</Typography>
								</>
							) : step === "device" ? (
								<>
									{deviceOptions ? (
										deviceNeedsRetry ? (
											<Button variant="contained" fullWidth type="button" onClick={handleDeviceSignIn} className="login-primary-btn login-verify-btn">
												Try again
											</Button>
										) : (
											<Typography className="login-form-subtitle">Waiting for this laptop...</Typography>
										)
									) : (
										<Typography className="login-form-subtitle">
											No device is registered for this account yet. Sign in with email or your authenticator app, then register this device from Profile.
										</Typography>
									)}
									<Box className="login-quiet-links">
										<button type="button" className="login-quiet-link" onClick={resetOtpState}>
											Back
										</button>
									</Box>
								</>
							) : step === "totp" ? (
								<>
									<TextField
										value={otp}
										onChange={(e) => setOtp(useBackupCode ? e.target.value : e.target.value.replace(/\D/g, "").slice(0, 6))}
										size="small"
										id="login-totp-input"
										placeholder={useBackupCode ? "xxxxx-xxxxx" : "000000"}
										type="text"
										autoComplete="one-time-code"
										inputProps={{ inputMode: useBackupCode ? "text" : "numeric", maxLength: useBackupCode ? 11 : 6 }}
										fullWidth
										className="login-input login-code-input"
									/>
									<Button variant="contained" fullWidth type="submit" className="login-primary-btn login-verify-btn">
										Verify
									</Button>
									<Box className="login-quiet-links">
										<button
											type="button"
											className="login-quiet-link"
											onClick={() => {
												setUseBackupCode((current) => !current);
												setOtp("");
											}}>
											{useBackupCode ? "Use authenticator app" : "Backup code"}
										</button>
										<button type="button" className="login-quiet-link" onClick={resetOtpState}>
											Back
										</button>
									</Box>
								</>
							) : (
								<>
									<TextField
										value={otp}
										onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
										size="small"
										id="login-otp-input"
										placeholder="000000"
										type="text"
										autoComplete="one-time-code"
										inputProps={{ inputMode: "numeric", maxLength: 6 }}
										fullWidth
										className="login-input login-code-input"
									/>
									{expiresAt ? (
										<Typography className="login-expiry-text">Code expires at {formatExpiry(expiresAt)}</Typography>
									) : null}
									<Button variant="contained" fullWidth type="submit" className="login-primary-btn login-verify-btn">
										Verify
									</Button>
									<Box className="login-quiet-links">
										<button type="button" className="login-quiet-link" onClick={handleResendOtp}>
											Resend email
										</button>
										<button type="button" className="login-quiet-link" onClick={resetOtpState}>
											Back
										</button>
									</Box>
								</>
							)}
						</Stack>
					</Box>
				</Box>
			</Box>
			<ForgotPasswordModal open={open} setOpen={setOpen} />
		</Box>
	);
};

export default Login;
